/**
 * Calendar invites that arrive by mail (contracts MailInvite): reading an outside organizer's iCalendar REQUEST or
 * CANCEL (Google, Outlook, Apple, another Bundle), answering it with an iTIP REPLY (RFC 5546 §3.2.3) as Gmail does, and
 * which state the invite card shows. The server parses and answers; every client shows the card from `inviteCardState`.
 */
import type { CalendarEvent, MailInvite, Recurrence, RsvpStatus } from "@bundle/contracts";
import { findIana } from "windows-iana";
import { DAY, HOUR, PARTSTAT, fold, seriesEnd, icsDate, icsDuration, icsLines, icsText, icsTime, icsUnescape, icsUtc, parseRrule } from "./calendar.ts";

/** An invite as the organizer sent it (the main VEVENT, or the one occurrence it's about). */
export type ParsedInvite = {
  method: "request" | "cancel";
  uid: string;
  sequence: number;
  title: string;
  start: number;
  end: number;
  allDay: boolean;
  /** The organizer's zone (IANA; Windows names from Outlook are mapped). */
  timeZone: string;
  description?: string;
  location?: string;
  conferenceUrl?: string;
  organizer: { email: string; name?: string };
  attendees: { email: string; name?: string; response: RsvpStatus; optional?: boolean }[];
  recurrence?: Recurrence;
  /** About one occurrence (RECURRENCE-ID): its original start. */
  occurrence?: number;
};

const RESPONSES: Record<string, RsvpStatus> = { ACCEPTED: "accepted", DECLINED: "declined", TENTATIVE: "tentative", "NEEDS-ACTION": "needs_action" };

/** A zone the runtime knows (IANA name or alias). */
function knownZone(zone: string): boolean {
  try {
    new Intl.DateTimeFormat("en-US", { timeZone: zone });
    return true;
  } catch {
    return false;
  }
}

/** A TZID as an IANA zone: as is, or Outlook's Windows name ("E. South America Standard Time") mapped through CLDR. */
export function ianaZone(tzid: string): string | undefined {
  const z = tzid.trim().replace(/^"|"$/g, "");
  if (z === "") return undefined;
  if (z.includes("/") && knownZone(z)) return z;
  const mapped = findIana(z)?.[0];
  if (mapped !== undefined && knownZone(mapped)) return mapped;
  return knownZone(z) ? z : undefined;
}

/** The first meeting link in the text: Google Meet, Zoom, Teams, Webex, Whereby, Jitsi. */
export function conferenceLink(...texts: (string | undefined)[]): string | undefined {
  const re = /https?:\/\/(?:meet\.google\.com\/[a-z0-9-]+|[\w.-]*zoom\.us\/(?:j|my|w)\/[^\s<>"')]+|teams\.microsoft\.com\/l\/meetup-join\/[^\s<>"')]+|teams\.live\.com\/meet\/[^\s<>"')]+|[\w-]+\.webex\.com\/[^\s<>"')]+|whereby\.com\/[^\s<>"')]+|meet\.jit\.si\/[^\s<>"')]+)/i;
  for (const t of texts) {
    const m = t === undefined ? null : re.exec(t);
    if (m !== null) return m[0];
  }
  return undefined;
}

// ---------------------------------------------------------------------------------------------------------------
// Replacing Google Meet with a Bundle meeting (docs/features/calendar-meet-swap.md)

/** A Google Meet room link (`https://meet.google.com/abc-defg-hij`, any query after it). */
const MEET_URL = /https?:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}(?![a-z-])(?:\?[^\s<>"')]*)?/gi;
/** The marker lines around Google Calendar's join block ("-::~:~::~:~:~ … ~::~:~::-"). */
const MEET_BLOCK_MARKER = /^-::~[:~]*::-\s*$/;
/** A line that is only a Meet link, maybe with Google's label ("Join with Google Meet: https://meet.google.com/…"). */
const MEET_LINE = /^\s*(?:(?:Join with )?Google Meet:?\s*)?https?:\/\/meet\.google\.com\/[a-z]{3}-[a-z]{4}-[a-z]{3}(?![a-z-])\S*\s*$/i;
/** The first line Bundle writes into a swapped description ("Join Bundle meeting: <url>"). */
const BUNDLE_LINE = /^Join Bundle meeting: \S+\s*$/;

/** An event's Google Meet link: the first one in its conference link, then its location, then its description. */
export function meetLink(e: { conferenceUrl?: string | undefined; location?: string | undefined; description?: string | undefined }): string | undefined {
  for (const t of [e.conferenceUrl, e.location, e.description]) {
    const m = t === undefined ? null : new RegExp(MEET_URL.source, "i").exec(t);
    if (m !== null) return m[0];
  }
  return undefined;
}

/**
 * Whether "Use a Bundle meeting" applies to the event for `me`: I organise it (not an outside organizer's copy; a
 * Google event only when I'm its organizer), it isn't cancelled, it has an occurrence that hasn't ended, and it has a
 * Google Meet link.
 */
export function meetSwapCandidate(
  e: Pick<CalendarEvent, "organizerId" | "externalOrganizer" | "cancelled" | "start" | "end" | "recurrence" | "timeZone" | "allDay" | "conferenceUrl" | "location" | "description">,
  me: string,
  now: number,
): boolean {
  if (e.organizerId !== me || e.externalOrganizer !== undefined || e.cancelled) return false;
  const end = seriesEnd(e);
  if (end !== undefined && end <= now) return false;
  return meetLink(e) !== undefined;
}

/**
 * A description without Google Calendar's Meet join block (the lines between two `-::~:~::~` markers: "Join with
 * Google Meet", "Join by phone", PINs, "Learn more about Meet", "Please do not edit this section") and without Meet
 * links (a line that is only one goes; one inside a sentence is taken out of it). Everything else is kept as it was;
 * the blank lines around a removed block close up to one.
 */
export function stripMeetJoinBlock(description: string): string {
  const eol = description.includes("\r\n") ? "\r\n" : "\n";
  const lines = description.split(/\r?\n/);
  const keep: string[] = [];
  let i = 0;
  let cut = false;
  while (i < lines.length) {
    const line = lines[i]!;
    if (MEET_BLOCK_MARKER.test(line)) {
      const close = lines.findIndex((l, j) => j > i && MEET_BLOCK_MARKER.test(l));
      if (close > i) {
        i = close + 1;
        cut = true;
        continue;
      }
    }
    if (MEET_LINE.test(line)) {
      i++;
      cut = true;
      continue;
    }
    const inline = line.replace(MEET_URL, "");
    if (inline !== line) {
      cut = true;
      keep.push(inline.replace(/[ \t]+$/, "").replace(/ {2,}/g, " "));
    } else keep.push(line);
    i++;
  }
  if (!cut) return description;
  // Close up the gaps the removed lines left: no blank lines at the ends, at most one blank line in a row.
  const out: string[] = [];
  for (const l of keep) {
    const blank = l.trim() === "";
    if (blank && (out.length === 0 || out[out.length - 1]!.trim() === "")) continue;
    out.push(l);
  }
  while (out.length > 0 && out[out.length - 1]!.trim() === "") out.pop();
  return out.join(eol);
}

/** The fields a Meet → Bundle swap changes (and a restore puts back). */
export type MeetFields = { location?: string | undefined; description?: string | undefined; conferenceUrl?: string | undefined };

/**
 * An event's place, notes and video link with a Bundle meeting instead of Google Meet: a location that is just the
 * Meet link becomes the Bundle link (any other location only has its Meet link replaced); the description starts with
 * "Join Bundle meeting: <url>" (`label` translates the words) followed by itself without Google's join block; the
 * conference link is the Bundle link. Swapping again gives the same result.
 */
export function swapMeetFields(f: MeetFields, bundleUrl: string, label = "Join Bundle meeting"): MeetFields {
  const out: MeetFields = { conferenceUrl: bundleUrl };
  if (f.location !== undefined) {
    const loc = f.location.trim();
    const only = new RegExp(`^${MEET_URL.source}$`, "i").test(loc);
    out.location = only ? bundleUrl : f.location.replace(MEET_URL, bundleUrl);
  }
  const rest = stripMeetJoinBlock(f.description ?? "").split(/\r?\n/);
  // Already swapped (a retry, a second run): its own first line isn't kept twice.
  while (rest.length > 0 && (BUNDLE_LINE.test(rest[0]!) || rest[0]!.startsWith(`${label}: `))) rest.shift();
  while (rest.length > 0 && rest[0]!.trim() === "") rest.shift();
  const eol = (f.description ?? "").includes("\r\n") ? "\r\n" : "\n";
  const body = rest.join(eol);
  out.description = body === "" ? `${label}: ${bundleUrl}` : `${label}: ${bundleUrl}${eol}${eol}${body}`;
  return out;
}

/** An address from a CAL-ADDRESS value (`mailto:ana@x.com`), lowercased; "" when it isn't one. */
function calAddress(value: string): string {
  const a = value.trim().replace(/^mailto:/i, "").trim().toLowerCase();
  return /^[^\s@]+@[^\s@]+$/.test(a) ? a : "";
}

/** A parameter's value (`CN="Ana Lima"` → Ana Lima). */
function param(params: string, name: string): string | undefined {
  const m = new RegExp(`;${name}=("([^"]*)"|[^;:]*)`, "i").exec(params);
  if (m === null) return undefined;
  const v = (m[2] ?? m[1] ?? "").trim();
  return v === "" ? undefined : v;
}

/**
 * The calendar's components as lists of [name+params, value] lines: each VEVENT, and each VTIMEZONE's STANDARD /
 * DAYLIGHT observances (Outlook's own zones).
 */
function components(text: string): { method: string; events: [string, string][][]; zones: Map<string, { offset: number; rule?: string; from: string; daylight: boolean }[]> } {
  let method = "";
  const events: [string, string][][] = [];
  const zones = new Map<string, { offset: number; rule?: string; from: string; daylight: boolean }[]>();
  const stack: string[] = [];
  let cur: [string, string][] = [];
  let zoneId = "";
  let obs: { offset: number; rule?: string; from: string; daylight: boolean } | undefined;
  for (const [k, v] of icsLines(text)) {
    const key = k.split(";")[0]!.toUpperCase();
    const val = v.trim().toUpperCase();
    if (key === "BEGIN") {
      stack.push(val);
      if (val === "VEVENT" && stack.length === 2) cur = [];
      if ((val === "STANDARD" || val === "DAYLIGHT") && stack[stack.length - 2] === "VTIMEZONE") obs = { offset: 0, from: "", daylight: val === "DAYLIGHT" };
      continue;
    }
    if (key === "END") {
      const top = stack.pop();
      if (top === "VEVENT" && stack.length === 1) events.push(cur);
      if ((top === "STANDARD" || top === "DAYLIGHT") && obs !== undefined && zoneId !== "") zones.set(zoneId, [...(zones.get(zoneId) ?? []), obs]);
      if (top === "STANDARD" || top === "DAYLIGHT") obs = undefined;
      continue;
    }
    const where = stack[stack.length - 1];
    if (stack.length === 1 && key === "METHOD") method = val;
    else if (where === "VEVENT" && stack.length === 2) cur.push([k, v]);
    else if (where === "VTIMEZONE" && key === "TZID") zoneId = v.trim();
    else if (obs !== undefined && key === "TZOFFSETTO") {
      const m = /^([+-])(\d{2})(\d{2})/.exec(v.trim());
      if (m !== null) obs.offset = (m[1] === "-" ? -1 : 1) * (Number(m[2]) * 60 + Number(m[3])) * 60_000;
    } else if (obs !== undefined && key === "RRULE") obs.rule = v.trim();
    else if (obs !== undefined && key === "DTSTART") obs.from = v.trim();
  }
  return { method, events, zones };
}

/** When a yearly VTIMEZONE observance (`FREQ=YEARLY;BYMONTH=10;BYDAY=-1SU`, at its DTSTART's local time) starts in a year. */
function onsetIn(year: number, o: { rule?: string; from: string }): number | undefined {
  const time = /T(\d{2})(\d{2})/.exec(o.from);
  const [h, mi] = time === null ? [0, 0] : [Number(time[1]), Number(time[2])];
  if (o.rule === undefined) {
    const d = /^(\d{4})(\d{2})(\d{2})/.exec(o.from);
    return d === null || Number(d[1]) !== year ? undefined : Date.UTC(year, Number(d[2]) - 1, Number(d[3]), h, mi);
  }
  const p = new Map(o.rule.split(";").map((kv) => kv.split("=") as [string, string]).map(([k, v]) => [k.toUpperCase(), v ?? ""]));
  const month = Number(p.get("BYMONTH") ?? "0");
  const byday = /^([+-]?\d)?(SU|MO|TU|WE|TH|FR|SA)$/.exec((p.get("BYDAY") ?? "").toUpperCase());
  if (month < 1 || byday === null) return undefined;
  const wd = ["SU", "MO", "TU", "WE", "TH", "FR", "SA"].indexOf(byday[2]!);
  const n = Number(byday[1] ?? "1");
  if (n > 0) {
    const first = new Date(Date.UTC(year, month - 1, 1)).getUTCDay();
    return Date.UTC(year, month - 1, 1 + ((wd - first + 7) % 7) + (n - 1) * 7, h, mi);
  }
  const last = new Date(Date.UTC(year, month, 0));
  return Date.UTC(year, month - 1, last.getUTCDate() - ((last.getUTCDay() - wd + 7) % 7) + (n + 1) * 7, h, mi);
}

/** A local wall time (as if UTC) in an Outlook VTIMEZONE: the observance in force then gives the offset. */
function fromZoneRules(localAsUtc: number, observances: { offset: number; rule?: string; from: string; daylight: boolean }[]): number {
  const year = new Date(localAsUtc).getUTCFullYear();
  let best: { at: number; offset: number } | undefined;
  for (const o of observances) {
    for (const y of [year - 1, year]) {
      const at = onsetIn(y, o);
      if (at !== undefined && at <= localAsUtc && (best === undefined || at > best.at)) best = { at, offset: o.offset };
    }
  }
  const offset = best?.offset ?? observances.find((o) => !o.daylight)?.offset ?? 0;
  return localAsUtc - offset;
}

/**
 * The invite in a text/calendar part: METHOD REQUEST (new or updated) or CANCEL, else undefined (a REPLY, COUNTER, a
 * plain PUBLISH feed). `defaultZone` reads floating times. TZIDs are IANA names (Google, Apple) or Windows names
 * (Outlook), mapped; a zone neither knows is read with the calendar's own VTIMEZONE rules.
 */
export function parseIcsInvite(text: string, defaultZone: string): ParsedInvite | undefined {
  const { method, events, zones } = components(text);
  if (method !== "REQUEST" && method !== "CANCEL") return undefined;
  const main = events.find((e) => !e.some(([k]) => /^RECURRENCE-ID/i.test(k))) ?? events[0];
  if (main === undefined) return undefined;
  const get = (name: string) => main.find(([k]) => k.split(";")[0]!.toUpperCase() === name);
  const uid = get("UID")?.[1]?.trim();
  const ds = get("DTSTART");
  if (uid === undefined || uid === "" || ds === undefined) return undefined;
  const time = (params: string, value: string, fallback: string): { ts: number; allDay: boolean; zone: string } | undefined => {
    const tzid = param(params, "TZID");
    if (tzid === undefined) return icsTime(params, value, fallback);
    const zone = ianaZone(tzid);
    if (zone !== undefined) return icsTime(`;TZID=${zone}`, value, zone);
    const local = icsTime("", value.replace(/Z$/, ""), "UTC");
    const rules = zones.get(tzid) ?? zones.get(tzid.replace(/^"|"$/g, ""));
    if (local === undefined || rules === undefined || local.allDay) return local === undefined ? undefined : { ...local, zone: fallback };
    return { ts: fromZoneRules(local.ts, rules), allDay: false, zone: fallback };
  };
  const start = time(ds[0], ds[1], defaultZone);
  if (start === undefined) return undefined;
  const de = get("DTEND");
  const dur = get("DURATION");
  const end = (de !== undefined ? time(de[0], de[1], start.zone)?.ts : dur !== undefined ? start.ts + (icsDuration(dur[1]) ?? 0) : undefined) ?? start.ts + (start.allDay ? DAY : HOUR);
  const org = get("ORGANIZER");
  const organizerEmail = org === undefined ? "" : calAddress(org[1]);
  if (organizerEmail === "") return undefined;
  const nameOf = (params: string, email: string) => {
    const cn = param(params, "CN");
    return cn === undefined || cn.toLowerCase() === email ? undefined : cn;
  };
  const organizerName = nameOf(org![0], organizerEmail);
  const attendees: ParsedInvite["attendees"] = [];
  for (const [k, v] of main) {
    if (k.split(";")[0]!.toUpperCase() !== "ATTENDEE") continue;
    const email = calAddress(v);
    if (email === "" || attendees.some((a) => a.email === email)) continue;
    const a: ParsedInvite["attendees"][number] = { email, response: RESPONSES[(param(k, "PARTSTAT") ?? "").toUpperCase()] ?? "needs_action" };
    const name = nameOf(k, email);
    if (name !== undefined) a.name = name;
    if (/^OPT-PARTICIPANT$/i.test(param(k, "ROLE") ?? "")) a.optional = true;
    attendees.push(a);
  }
  const out: ParsedInvite = {
    method: method === "CANCEL" || /^CANCELLED$/i.test(get("STATUS")?.[1]?.trim() ?? "") ? "cancel" : "request",
    uid,
    sequence: Math.max(0, Number(get("SEQUENCE")?.[1]?.trim() ?? "0") || 0),
    title: icsUnescape(get("SUMMARY")?.[1] ?? "").trim() || "(No title)",
    start: start.ts,
    end: end > start.ts ? end : start.ts + (start.allDay ? DAY : HOUR),
    allDay: start.allDay,
    timeZone: start.zone,
    organizer: organizerName === undefined ? { email: organizerEmail } : { email: organizerEmail, name: organizerName },
    attendees,
  };
  const desc = get("DESCRIPTION")?.[1];
  if (desc !== undefined && desc.trim() !== "") out.description = icsUnescape(desc);
  const loc = get("LOCATION")?.[1];
  if (loc !== undefined && loc.trim() !== "") out.location = icsUnescape(loc).trim();
  const conf = get("X-GOOGLE-CONFERENCE")?.[1]?.trim() ?? get("X-MICROSOFT-SKYPETEAMSMEETINGURL")?.[1]?.trim() ?? conferenceLink(out.location, out.description);
  if (conf !== undefined && /^https?:\/\//i.test(conf)) out.conferenceUrl = conf;
  const rid = get("RECURRENCE-ID");
  if (rid !== undefined) {
    const at = time(rid[0], rid[1], start.zone)?.ts;
    if (at !== undefined) out.occurrence = at;
  }
  const rr = get("RRULE");
  const rec = rr === undefined ? undefined : parseRrule(rr[1], start.zone);
  if (rec !== undefined) {
    const ex = main
      .filter(([k]) => k.split(";")[0]!.toUpperCase() === "EXDATE")
      .flatMap(([k, v]) => v.split(",").map((x) => time(k, x, start.zone)?.ts))
      .filter((x): x is number => x !== undefined);
    if (ex.length > 0) rec.exdates = ex.slice(0, 500);
    out.recurrence = rec;
  }
  return out;
}

/** Which guest on the invite I am: the first of my addresses (the mailbox's, then its aliases) that it names. */
export function inviteGuestAddress(invite: Pick<ParsedInvite, "attendees">, myAddresses: readonly string[]): string | undefined {
  const mine = myAddresses.map((a) => a.toLowerCase());
  return mine.find((a) => invite.attendees.some((x) => x.email === a));
}

/** What an answer to an invite says (iTIP REPLY): who answers, how, and for which occurrence. */
export type IcsReply = {
  uid: string;
  sequence: number;
  stamp: number;
  organizer: { email: string; name?: string | undefined };
  attendee: { email: string; name?: string | undefined };
  response: Exclude<RsvpStatus, "needs_action">;
  title: string;
  start: number;
  end: number;
  allDay: boolean;
  timeZone: string;
  /** Just this occurrence (its original start). */
  occurrence?: number | undefined;
};

/**
 * The REPLY a calendar app sends when I answer (Google's shape): METHOD:REPLY, the invite's UID and SEQUENCE, my
 * ATTENDEE line with PARTSTAT, and RECURRENCE-ID when it's about one occurrence. CRLF line ends, folded.
 */
export function buildIcsReply(r: IcsReply): string {
  const cn = (name: string | undefined) => (name === undefined || name === "" ? "" : `;CN="${name.replace(/"/g, "'")}"`);
  const when = (ts: number) => (r.allDay ? `;VALUE=DATE:${icsDate(ts, r.timeZone)}` : `:${icsUtc(ts)}`);
  const lines = [
    "BEGIN:VCALENDAR",
    "PRODID:-//Bundle//Calendar//EN",
    "VERSION:2.0",
    "CALSCALE:GREGORIAN",
    "METHOD:REPLY",
    "BEGIN:VEVENT",
    `UID:${r.uid}`,
    ...(r.occurrence === undefined ? [] : [`RECURRENCE-ID${when(r.occurrence)}`]),
    `SEQUENCE:${r.sequence}`,
    `DTSTAMP:${icsUtc(r.stamp)}`,
    `DTSTART${when(r.start)}`,
    `DTEND${when(r.end)}`,
    `SUMMARY:${icsText(r.title)}`,
    `ORGANIZER${cn(r.organizer.name)}:mailto:${r.organizer.email}`,
    `ATTENDEE${cn(r.attendee.name)};PARTSTAT=${PARTSTAT[r.response]}:mailto:${r.attendee.email}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ];
  return `${lines.map(fold).join("\r\n")}\r\n`;
}

/** The REPLY's subject, as Google and Outlook write it ("Accepted: Weekly sync"). */
export function inviteReplySubject(response: Exclude<RsvpStatus, "needs_action">, title: string): string {
  return `${response === "accepted" ? "Accepted" : response === "declined" ? "Declined" : "Tentatively accepted"}: ${title}`;
}

/** A zone's city for "4:00 PM in São Paulo": the IANA name's last part ("America/Sao_Paulo" → "Sao Paulo"). */
export function zoneCity(zone: string): string {
  const last = zone.split("/").pop() ?? zone;
  return last.replace(/_/g, " ");
}

/**
 * What the invite card shows:
 * - `cancelled`: the organizer cancelled it (struck-through title, removed from my calendar);
 * - `superseded`: a newer version arrived later in my mail (this card is out of date);
 * - `updated`: an update that moved it (old time crossed out), still mine to answer;
 * - `answered`: I said yes, no or maybe ("You're going · sent to …", Change);
 * - `pending`: not answered yet (Yes / No / Maybe).
 */
export function inviteCardState(invite: Pick<MailInvite, "method" | "superseded" | "movedFrom" | "myResponse">): "pending" | "answered" | "updated" | "cancelled" | "superseded" {
  if (invite.method === "cancel") return "cancelled";
  if (invite.superseded === true) return "superseded";
  if (invite.movedFrom !== undefined) return "updated";
  return invite.myResponse === "needs_action" ? "pending" : "answered";
}
